import { getCurrentConfig, PAYMENT_STATUS } from "../constants/razorpay"
import { ordersService } from "./firebase"

export interface PaymentRequest {
  orderId: string
  amount: number
  userId: string
  paymentMethod?: "online" | "cod" | "razorpay"
  mobileNumber?: string
  email?: string
  name?: string
}

export interface PaymentResponse {
  success: boolean
  orderId?: string
  paymentId?: string
  error?: string
}

export interface VerificationRequest {
  orderId: string
  paymentId: string
  signature: string
}

class RazorpayService {
  private config = getCurrentConfig()
  private apiBase = (() => {
    const explicitApi = import.meta.env.VITE_RAZORPAY_API_URL || import.meta.env.VITE_API_BASE_URL
    if (explicitApi) return explicitApi
    return ""
  })()

  // Create Razorpay order
  async createOrder(paymentData: PaymentRequest): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const apiUrl = `${this.apiBase}/api/create-razorpay-order`

      const orderRequest = {
        amount: paymentData.amount, // send full amount
        currency: "INR",
        receipt: `receipt_${paymentData.orderId}`,
        notes: {
          orderId: paymentData.orderId,
          userId: paymentData.userId,
        },
        paymentMethod: paymentData.paymentMethod, // "cod" for COD
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderRequest),
      })

      if (!response.ok) {
        const errorText = await response.text()
        const missingRouteHint =
          response.status === 404
            ? " Endpoint not found. Check VITE_RAZORPAY_API_URL / backend route."
            : ""
        return {
          success: false,
          error: `Order API error (${response.status}): ${errorText.slice(0, 120)}${missingRouteHint}`,
        }
      }

      const data = await response.json()

      if (data.success && data.order) {
        const order = data.order

        // Store order record in Firebase
        await this.storeOrderRecord({
          orderId: paymentData.orderId,
          razorpayOrderId: order.id,
          userId: paymentData.userId,
          amount: paymentData.amount,
          status: PAYMENT_STATUS.PENDING,
          createdAt: new Date().toISOString(),
        })

        return {
          success: true,
          data: {
            orderId: order.id,
            amount: order.amount,
            currency: order.currency,
            receipt: order.receipt,
          },
        }
      }

      return {
        success: false,
        error: data.error || "Order creation failed",
      }
    } catch (error) {
      console.error("Order creation error:", error)
      return {
        success: false,
        error: "Payment service unavailable",
      }
    }
  }

  // Verify payment signature
  async verifyPayment(verificationData: VerificationRequest): Promise<PaymentResponse> {
    try {
      const { orderId, paymentId, signature } = verificationData

      const apiUrl = `${this.apiBase}/api/verify-razorpay-payment`

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          razorpay_order_id: orderId,
          razorpay_payment_id: paymentId,
          razorpay_signature: signature,
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        return {
          success: false,
          error: `Verification API error (${response.status}): ${errorText.slice(0, 120)}`,
        }
      }

      const data = await response.json()

      if (data.success) {
        // Payment verified successfully
        await this.handleSuccessfulPayment(orderId, paymentId)
        return {
          success: true,
          orderId,
          paymentId,
        }
      } else {
        // Payment verification failed
        await this.handleFailedPayment(orderId, paymentId)
        return {
          success: false,
          error: data.error || "Payment verification failed",
        }
      }
    } catch (error) {
      console.error("Payment verification error:", error)
      return {
        success: false,
        error: "Verification service unavailable",
      }
    }
  }

  // Store order record in Firebase
  private async storeOrderRecord(orderData: any): Promise<void> {
    try {
      await ordersService.addRazorpayOrderRecord(orderData)
    } catch (error) {
      console.error("Error storing order record:", error)
    }
  }

  // Handle successful payment
  private async handleSuccessfulPayment(orderId: string, paymentId: string): Promise<void> {
    try {
      // Get order record to find original order ID
      const orderRecord = await ordersService.getRazorpayOrderRecord(orderId)
      if (orderRecord) {
        // Update order status
        await ordersService.updateOrderPaymentStatus(orderRecord.orderId, {
          paymentStatus: "completed",
          paymentId,
          razorpayOrderId: orderId,
          paymentMethod: "razorpay",
          paymentDetails: { paymentId, orderId },
        })

        // Update revenue analytics
        await this.updateRevenueAnalytics(orderRecord.amount)
      }
    } catch (error) {
      console.error("Error handling successful payment:", error)
    }
  }

  // Handle failed payment
  private async handleFailedPayment(orderId: string, paymentId: string): Promise<void> {
    try {
      const orderRecord = await ordersService.getRazorpayOrderRecord(orderId)
      if (orderRecord) {
        await ordersService.updateOrderPaymentStatus(orderRecord.orderId, {
          paymentStatus: "failed",
          paymentId,
          razorpayOrderId: orderId,
          paymentMethod: "razorpay",
          paymentDetails: { paymentId, orderId },
        })
      }
    } catch (error) {
      console.error("Error handling failed payment:", error)
    }
  }

  // Update revenue analytics
  private async updateRevenueAnalytics(amount: number): Promise<void> {
    try {
      const today = new Date().toISOString().split("T")[0]
      const month = new Date().toISOString().slice(0, 7)

      // Update daily revenue
      await ordersService.updateRevenueAnalytics("daily", today, amount)

      // Update monthly revenue
      await ordersService.updateRevenueAnalytics("monthly", month, amount)

      // Update total revenue
      await ordersService.updateRevenueAnalytics("total", "all-time", amount)
    } catch (error) {
      console.error("Error updating revenue analytics:", error)
    }
  }

  // Get Razorpay checkout options
  getCheckoutOptions(orderData: any, userData: any) {
    return {
      key: this.config.KEY_ID,
      amount: orderData.amount,
      currency: orderData.currency,
      name: "Anmol Furniture Bazaar",
      description: `Order #${orderData.receipt}`,
      order_id: orderData.orderId,
      prefill: {
        name: userData.name || "",
        email: userData.email || "",
        contact: userData.mobileNumber || "",
      },
      notes: {
        orderId: orderData.receipt,
      },
      theme: {
        color: "#2563eb",
      },
      callback_url: `${window.location.origin}/payment/callback`,
      redirect: false,
    }
  }
}

export const razorpayService = new RazorpayService()
